/**
 * 联机会话的装配与生命周期 —— 整个客户端**唯一**建连的地方。
 *
 * 顺序不能换（§11.7 / room/index.ts 的接线说明）：
 *
 *   getAuthSession()              // 大厅已经在后台匿名注册过，这里拿同一个单例
 *   → ensureAccessToken()         // 没账号就注册，有就刷新；并发调用单飞
 *   → fetchServerConfig()         // ★ ws 地址由 GET /api/config 下发，绝不硬编码
 *   → createWsTransport(...)
 *   → setLobbyPort(transport)     // 不接这一步，大厅两个按钮会一直说「连不上服务器」
 *   → startTableMirror()          // 座位 / 房间码 / 连接相位 → tableStore
 *   → transport.connect()
 *
 * `startRoomVmMirror()`（PlayerView → 牌桌内容）由 `RoomScreen` 自己起，这里不碰。
 *
 * ★ 为什么「离开房间」要**重建连接**而不是清一下 store：
 *   服务端不会给离房的连接再推任何东西，但客户端手里那份 `view` 还在。
 *   直接 `useNetStore.reset()` 会把 `appliedSeq` 归零，而这条 WS 会话的 `seq`
 *   还在 120 上继续涨 —— `classifyFrame` 从此每一帧都判 gap，整条连接永久失聪。
 *   重建连接则会拿到一个新的服务端会话（`welcome{resumed:false}` → 游标归零），
 *   这是协议里唯一干净的「从头开始」。
 */
import { useEffect, useState } from 'react'
import type { AuthSession } from '../auth/session.js'
import { getAuthSession } from '../lobby/useAuth.js'
import { setLobbyPort, useLobbyFlow } from '../lobby/lobbyFlow.js'
import { useTableStore, useUiStore } from '../store/index.js'
import { startTableMirror, useNetStore } from '../transport/applyMessage.js'
import { createWsTransport, fetchServerConfig, type WsTransport } from '../transport/WsTransport.js'

import { apiBaseUrl, resolveWsUrl } from './netUrl.js'
import { startRoomRebind } from './roomRebind.js'
import { clearRoomRoute, startRoomRouteSync } from './route.js'

/**
 * 开发期调试出口：`__poker.net.getState()` 能在浏览器控制台里看到真源。
 * 生产构建里 `import.meta.env.DEV` 为 false，整段会被摇掉。
 * ★ 只读句柄，任何生产逻辑都不准依赖它。
 */
function exposeDevStores(): void {
  if (!import.meta.env.DEV) return
  ;(globalThis as unknown as Record<string, unknown>)['__poker'] = {
    net: useNetStore,
    table: useTableStore,
    ui: useUiStore,
    lobby: useLobbyFlow,
    session: (): OnlineSession | null => session,
  }
}

export type BootStatus = 'idle' | 'connecting' | 'ready' | 'error'

export interface OnlineSession {
  readonly transport: WsTransport
  readonly auth: AuthSession
  readonly wsUrl: string
}

export interface BootOptions {
  /** HTTP API 基址。默认页面同源（dev 下 vite 把 /api 代理到服务端）。 */
  readonly baseUrl?: string
  /** 页面 origin，用来归一 ws 地址。默认 `location.origin`。 */
  readonly origin?: string
  readonly fetchImpl?: typeof fetch
}

let session: OnlineSession | null = null
let inFlight: Promise<OnlineSession> | null = null
let stopMirrors: (() => void) | null = null

export function onlineSession(): OnlineSession | null {
  return session
}

/**
 * 建立（或复用）联机会话。并发调用共用同一个飞行中的 Promise ——
 * React 18+ 的 StrictMode 会把挂载副作用跑两遍，没有这层去重就会建两条 WS。
 */
export function startOnlineSession(opts: BootOptions = {}): Promise<OnlineSession> {
  if (session !== null) return Promise.resolve(session)
  inFlight ??= boot(opts).finally(() => {
    inFlight = null
  })
  return inFlight
}

async function boot(opts: BootOptions): Promise<OnlineSession> {
  const baseUrl = opts.baseUrl ?? apiBaseUrl()
  const origin = opts.origin ?? apiBaseUrl()
  const auth = getAuthSession({ baseUrl, ...(opts.fetchImpl === undefined ? {} : { fetchImpl: opts.fetchImpl }) })
  await auth.ensureAccessToken()

  const made = await bootReal(opts, baseUrl, origin, auth)

  setLobbyPort(made.transport)
  // 两条镜像里的「壳」那条由串联层起；「内容」那条在 RoomScreen 里
  const unTable = startTableMirror()
  const unRoute = startRoomRouteSync()
  const unAccount = startWelcomeAccountSync(auth)
  // 重连没能回到房间时：自动用房间码回去，回不去就回大厅说清原因（必须在 connect 之前订上）
  const unRebind = startRoomRebind(made.transport, {
    giveUp: (text) => {
      leaveRoomAndReset()
      useUiStore.getState().showToast(text, 'warn')
    },
  })
  stopMirrors = () => {
    unTable()
    unRoute()
    unAccount()
    unRebind()
  }

  session = made
  exposeDevStores()
  await made.transport.connect()
  return made
}

async function bootReal(
  opts: BootOptions,
  baseUrl: string,
  origin: string,
  auth: AuthSession,
): Promise<OnlineSession> {
  const cfg = await fetchServerConfig(baseUrl, opts.fetchImpl ?? ((...a) => globalThis.fetch(...a)))
  const wsUrl = resolveWsUrl(cfg.wsUrl, origin)
  const transport = createWsTransport(wsUrl, () => auth.ensureAccessToken(), {
    refreshToken: () => auth.forceRefresh(),
    deviceId: auth.deviceId(),
  })
  return { transport, auth, wsUrl }
}

/** 彻底停掉：关连接、解绑大厅、停镜像。登出与测试用。 */
export function stopOnlineSession(): void {
  session?.transport.close()
  session = null
  inFlight = null
  stopMirrors?.()
  stopMirrors = null
  setLobbyPort(null)
}

/**
 * 身份换人了（邮箱登录 / 兑换邮件链接之后）：把整条连接换掉。
 *
 * ★ 必须重建而不是复用。`auth.adopt()` 只换了本地这份令牌，**已经建好的那条 WS
 *   仍然在服务端注册成旧账号的会话** —— 不换的话，用户输完码看到「已找回」，
 *   接着建房/进房还是以旧身份进去，座位、筹码一个也回不来。
 *
 * 顺序照抄 `leaveRoomAndReset`：先抹地址栏，再清房间态，最后重连。
 * 旧账号的房间态一律丢掉 —— 它属于另一个人了。
 */
export async function restartOnlineSession(opts: BootOptions = {}): Promise<void> {
  clearRoomRoute()
  stopOnlineSession()
  // reset 而不是 leaveRoom：这是换会话，游标本来就该归零
  useNetStore.getState().reset()
  useTableStore.getState().reset()
  useLobbyFlow.getState().reset()
  useUiStore.getState().setView('lobby')
  await startOnlineSession(opts)
}

/**
 * 离开房间：把本机所有「房间态」清干净，回大厅。
 *
 * 调用方负责先发 `leaveRoom`（`RoomHeader` 自己发了）——这里只管善后。
 *
 * ★ **不重建连接**。曾经这么做过，结果是：`close()` 的清理与新连接的 `hello`
 *   在服务端撞车（同一个 `sid`），新连接注册完就被旧连接的关闭回调抹掉，
 *   之后发出去的 `joinRoom` 石沉大海、界面永远停在「正在进房…」。
 *   正确做法是连接原样留着（协议本来就允许一条连接换房间），
 *   只清掉房间范围的本地状态 —— `useNetStore.leaveRoom()` 保留游标与会话。
 *
 * ★ 顺序有讲究：先抹地址栏再切视图，否则大厅挂载的那一帧会读到 `/r/CODE`，
 *   把刚离开的人原地弹回去。
 */
export function leaveRoomAndReset(): void {
  clearRoomRoute()
  useNetStore.getState().leaveRoom()
  // 镜像是 diff 驱动的，上一行已经把清空推给了 tableStore；这一下只是兜底
  useTableStore.getState().reset()
  useLobbyFlow.getState().noteLeft()
  useUiStore.getState().setView('lobby')
}

// ============================================================================
// React 绑定
// ============================================================================

export interface BootView {
  readonly status: BootStatus
  readonly error: string | null
  readonly session: OnlineSession | null
  retry(): void
}

function messageOf(e: unknown): string {
  return e instanceof Error && e.message !== '' ? e.message : '连不上服务器'
}

/**
 * 挂载即建连。失败不阻塞界面：大厅照常可用，只是按钮会说连不上，
 * 并给一个「重试」——把网络错误做成全屏模态是最劝退的做法。
 */
export function useOnlineBoot(opts: BootOptions = {}): BootView {
  const [status, setStatus] = useState<BootStatus>(() => (onlineSession() === null ? 'connecting' : 'ready'))
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let alive = true
    setStatus(onlineSession() === null ? 'connecting' : 'ready')
    startOnlineSession(opts).then(
      () => {
        if (!alive) return
        setStatus('ready')
        setError(null)
      },
      (e: unknown) => {
        if (!alive) return
        setStatus('error')
        setError(messageOf(e))
      },
    )
    return () => {
      alive = false
    }
    // opts 是调用方的字面量，每次渲染都换引用；依赖它会变成无限重连
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt])

  return {
    status,
    error,
    session: onlineSession(),
    retry: () => setAttempt((n) => n + 1),
  }
}

/**
 * 每次 welcome 带回的账号资料（服务端在握手时现读的库）同步给本机的账号缓存。
 *
 * ★ 本机缓存平时只在换票时更新（约 15 分钟一次）。同一账号在别的设备上改了声音或昵称，
 *   这台重连时房间已经按新资料记了，本机却还拿旧的：自己点短语播的是旧声音，同桌听到的是新声音。
 * ★ 只认同一个账号：换号时旧连接晚到的 welcome 不能把当前身份改回去。
 */
export function startWelcomeAccountSync(auth: Pick<AuthSession, 'account' | 'updateAccount'>): () => void {
  return useNetStore.subscribe((s, prev) => {
    const next = s.session?.account
    if (next === undefined || next === prev.session?.account) return
    const cur = auth.account()
    if (cur === null || cur.id !== next.id) return
    if (cur.voice === next.voice && cur.nickname === next.nickname && cur.avatarSeed === next.avatarSeed) return
    auth.updateAccount(next)
  })
}
